import type { ReactNode } from 'react';
import { InfoPopover } from './InfoPopover';
import styles from './KpiTile.module.css';

interface Props {
  label: string;
  value: string | number;
  qualifier?: string;
  tone?: 'neutral' | 'positive' | 'negative' | 'warning';
  spark?: number[];
  /** Optional explainer surfaced via an info icon next to the label. */
  tooltip?: ReactNode;
  className?: string;
}

const toneToColor: Record<NonNullable<Props['tone']>, string> = {
  neutral: 'var(--fg-primary)',
  positive: 'var(--tia-positive)',
  negative: 'var(--tia-negative)',
  warning: 'var(--tia-warning)',
};

export function KpiTile({ label, value, qualifier, tone = 'neutral', spark, tooltip, className }: Props) {
  const color = toneToColor[tone];
  return (
    <div className={[styles.tile, className].filter(Boolean).join(' ')}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {tooltip && <InfoPopover label={`What is ${label}?`} content={tooltip} />}
      </div>
      <div className={styles.valueRow}>
        <div className={styles.value} style={{ color }}>
          {value}
        </div>
        {spark && <Sparkline data={spark} color={color} />}
      </div>
      {qualifier && (
        <div className={styles.qualifier} style={{ color }}>
          {qualifier}
        </div>
      )}
    </div>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 48;
      const y = 16 - ((v - min) / range) * 16;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <svg width={48} height={16} viewBox="0 0 48 16" aria-hidden="true" className={styles.spark}>
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
